import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Phone } from "lucide-react";

import { CtaBand, PageHero } from "@/components/page-parts";
import { BUSINESS_LOCATIONS } from "@/lib/business-locations";
import { FIRM, FIRM_ADDRESS } from "@/lib/firm";
import { ServiceReading } from "@/components/service-reading";

const description =
  "Business and commercial lawyers in Golden Beach, serving Caloundra and the Sunshine Coast. Business sales, purchases, commercial and retail leases, guarantees.";

const collectionSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Business lawyers on the Caloundra coast",
  description,
  isPartOf: { "@type": "WebSite", name: FIRM.name },
  hasPart: BUSINESS_LOCATIONS.map((l) => ({
    "@type": "WebPage",
    name: l.title,
    url: `https://coastsidelaw.com.au/business-lawyers/${l.slug}`,
  })),
};

export const Route = createFileRoute("/business-lawyers/")({
  head: () => ({
    meta: [
      { title: fitTitle(`Business Sale & Commercial Lawyers, Caloundra Coast | ${FIRM.name}`) },
      { name: "description", content: fitDescription(description) },
      { property: "og:title", content: fitTitle(`Business lawyers, Caloundra and Golden Beach | ${FIRM.name}`) },
      { property: "og:description", content: fitDescription(description) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://coastsidelaw.com.au/business-lawyers" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/business-lawyers" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(collectionSchema) }],
  }),
  component: BusinessLawyersIndex,
});

function BusinessLawyersIndex() {
  return (
    <>
      <PageHero
        personSlug="michael-klein"
        eyebrow="Business & commercial"
        title="Business and commercial lawyers on the Sunshine Coast"
        lead={`From our office at ${FIRM_ADDRESS}, Michael Klein assists business owners buying, selling and leasing across Caloundra, Golden Beach, Pelican Waters and the Sunshine Coast. Tell us what you are planning and what has already been signed; we confirm the scope and fee before starting.`}
      />

      <section className="mx-auto max-w-5xl px-5 py-16">
        <h2 className="font-serif text-3xl text-foreground">Advice before the business commits</h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          A business lawyer should look at how the documents work together, not just the sale
          price. The contract, premises lease, landlord consent, guarantees and settlement dates
          may each affect whether the deal can proceed as intended. Send the documents together
          and identify any deposit paid, deadline or work already promised.
        </p>
        <ul className="mt-6 space-y-3 text-base text-primary">
          <li><Link to="/business-sales" className="font-semibold hover:underline">Business sales and purchases — contract review and settlement</Link></li>
          <li><Link to="/commercial-conveyancing" className="font-semibold hover:underline">Commercial and industrial property transactions</Link></li>
          <li><Link to="/retail-shop-leases" className="font-semibold hover:underline">Retail shop leases and Form 12 advice</Link></li>
          <li><Link to="/independent-legal-advice" className="font-semibold hover:underline">Personal guarantees and independent legal advice</Link></li>
        </ul>
        <h2 className="mt-12 font-serif text-3xl text-foreground">Commercial and industrial lease advice</h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Whether you need a lease lawyer for a new tenancy, a renewal or an assignment, bring
          the full lease, proposed changes and any disclosure or incentive documents. We discuss
          permitted use, rent reviews, outgoings, repairs, fit-out obligations, options, make-good
          and guarantees. Retail leasing protections depend on the premises and the statutory
          rules, not just the word “commercial” in the document; we check the applicable position.
        </p>
        <h2 className="mt-12 font-serif text-3xl text-foreground">A local commercial lawyer</h2>
        <p className="mt-4 mb-8 text-base leading-relaxed text-muted-foreground">
          Meet with us at Golden Beach or discuss the documents by phone or video. For example,
          a hypothetical Caloundra café buyer may need the sale contract and lease assignment
          coordinated before settlement, while a Pelican Waters tenant may need to check fit-out
          and exit obligations before accepting a lease. These are illustrations, not client
          matters. Choose your area below for local business context.
        </p>
        <ul className="grid gap-4 sm:grid-cols-2">
          {BUSINESS_LOCATIONS.map((l) => (
            <li key={l.slug}>
              <Link
                to="/business-lawyers/$suburb"
                params={{ suburb: l.slug }}
                className="group flex h-full flex-col rounded-sm border border-border bg-card p-6 transition-colors hover:border-primary"
              >
                <span className="font-serif text-xl text-foreground group-hover:text-primary">
                  {l.name}
                </span>
                <span className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {l.sectors.slice(0, 3).join(" · ")}
                </span>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                  Business lawyers in {l.name}
                  <ArrowRight className="size-4" aria-hidden="true" />
                </span>
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-12 rounded-sm border border-border bg-secondary/40 p-6">
          <h2 className="font-serif text-2xl text-foreground">Before you sign anything</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Download our Queensland business sale and purchase checklist, or send us the contract
            and we will quote a fixed fee to review it.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/business-sale-checklist"
              className="inline-flex items-center justify-center rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              Get the checklist
            </Link>
            <a
              href={FIRM.phoneHref}
              className="inline-flex items-center gap-2 rounded-sm border border-border px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
            >
              <Phone className="size-4 text-primary" aria-hidden="true" />
              Call {FIRM.phone}
            </a>
          </div>
        </div>
        <ServiceReading service="business" />
      </section>

      <CtaBand
        title="Buying or selling a business on the Coast?"
        body="Tell us where the deal is up to and we will tell you what needs to happen next — and what it will cost."
      />
    </>
  );
}
